import { type GeoTrip } from 'types/geo.types';
import {
  compareMasterAlgoVsApprovedPlanKPI,
  mergeDispatchPlans,
} from '../utils/dataFormatters';
import { useMemo } from 'react';
import {
  aggregateSchedulePlanByBumperNumber,
  transformMasterAlgoToPlanDataByBumperNo,
} from 'views/FleetPlanner/utils/dataFormatters';
import { useAppSelector } from 'store/hook';
import {
  AccessTime,
  Inventory,
  LocalGasStation,
  Speed,
  TrendingUp,
} from '@mui/icons-material';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { FleetKPIStackedBarChart } from './FleetKPIStackedBarChart';
import {
  type DispatchPlanMaterialDumpResponseType,
  type DispatchPlanTravelTimeMetricsResponseType,
  type DispatchPlanTripsResponseType,
  type DispatchPlanFuelUsageMetricsResponseType,
} from 'views/FleetPlanner/CreateDispatchPlan/Steps/types/steps.types';
import { FleetKPICard } from './FleetKPICard';
import { getPlanTimeRange } from '../utils/dataGetters';
import dayjs from 'dayjs';

import utc from 'dayjs/plugin/utc';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { convertLitersToGallons } from 'utils/helpers/unitSystem';
import { useTranslation } from 'react-i18next';

dayjs.extend(utc);

const KPICardWrapper = ({
  masterAlgoFuelUsageByBumperNo,
  masterAlgoMaterialDumpByBumperNo,
  masterAlgoTravelTimeByBumperNo,
  masterAlgoTripsCountByBumperNo,
  approvedPlanFuelUsageByBumperNo,
  approvedPlanMaterialDumpByBumperNo,
  approvedPlanTravelTimeByBumperNo,
  approvedPlanTripsCountByBumperNo,
}: {
  masterAlgoFuelUsageByBumperNo: Record<
    string,
    DispatchPlanFuelUsageMetricsResponseType
  >;
  masterAlgoMaterialDumpByBumperNo: Record<
    string,
    DispatchPlanMaterialDumpResponseType
  >;
  masterAlgoTravelTimeByBumperNo: Record<
    string,
    DispatchPlanTravelTimeMetricsResponseType
  >;
  masterAlgoTripsCountByBumperNo: Record<string, DispatchPlanTripsResponseType>;
  approvedPlanFuelUsageByBumperNo: Record<
    string,
    DispatchPlanFuelUsageMetricsResponseType
  >;
  approvedPlanMaterialDumpByBumperNo: Record<
    string,
    DispatchPlanMaterialDumpResponseType
  >;
  approvedPlanTravelTimeByBumperNo: Record<
    string,
    DispatchPlanTravelTimeMetricsResponseType
  >;
  approvedPlanTripsCountByBumperNo: Record<
    string,
    DispatchPlanTripsResponseType
  >;
}) => {
  const { t } = useTranslation();
  const { unitSystem } = useCustomerInfo();
  const result = compareMasterAlgoVsApprovedPlanKPI(
    {
      fuel_usage: masterAlgoFuelUsageByBumperNo,
      material_dumped: masterAlgoMaterialDumpByBumperNo,
      travel_time: masterAlgoTravelTimeByBumperNo,
      trips: masterAlgoTripsCountByBumperNo,
    },
    {
      fuel_usage: approvedPlanFuelUsageByBumperNo,
      material_dumped: approvedPlanMaterialDumpByBumperNo,
      travel_time: approvedPlanTravelTimeByBumperNo,
      trips: approvedPlanTripsCountByBumperNo,
    }
  );

  const kpis = [
    {
      icon: TrendingUp,
      label: t('production_rate'),
      planValue: result.plan.productivity.toFixed(2),
      planUnit: 'T/hr',
      actualValue: result.actual.productivity.toFixed(2),
      actualUnit: 'T/hr',
      delta:
        result.plan.productivity > 0
          ? `${result.deltas.productivity >= 0 ? '' : '▼'} ${
              result.deltas.productivity > 0 ? '+' : ''
            } ${result.deltas.productivity.toFixed(2)}`
          : '-',
      deltaType: result.deltas.productivity >= 0 ? 'grey' : 'red', // Good
    },
    {
      icon: Inventory,
      label: t('tons_moved'),
      planValue: result.plan.totalTons.toFixed(1),
      planUnit: 'T',
      actualValue: result.actual.totalTons.toFixed(1),
      actualUnit: 'T',
      delta:
        result.plan.totalTons > 0
          ? `${result.deltas.tons >= 0 ? '' : '▼'} ${
              result.deltas.tons > 0 ? '+' : ''
            } ${result.deltas.tons.toFixed(1)}`
          : '-',
      deltaType: result.deltas.tons >= 0 ? 'grey' : 'red', // Good
    },
    {
      icon: AccessTime,
      label: t('total_time'),
      planValue: result?.plan?.totalMinutes?.toFixed(0),
      planUnit: 'mins',
      actualValue: result?.actual?.totalMinutes?.toFixed(0),
      actualUnit: 'mins',
      delta:
        result.plan.totalMinutes > 0
          ? `${
              result.deltas.minutes > 0 ? '▲' : ''
            } ${result.deltas.minutes.toFixed(0)}`
          : '-',
      deltaType:
        result.plan.totalMinutes > 0
          ? result.deltas.minutes > 0
            ? 'red'
            : 'grey'
          : 'grey', // Bad
    },
    {
      icon: Speed,
      label: t('fuel_per_ton'),
      planValue: result.plan.fuelPerTon.toFixed(4),
      planUnit: unitSystem === 'metric' ? 'L/T' : 'Gals/T',
      actualValue: result.actual.fuelPerTon.toFixed(4),
      actualUnit: unitSystem === 'metric' ? 'L/T' : 'Gals/T',
      delta:
        result.plan.fuelPerTon > 0
          ? `${
              result.deltas.fuelPerTon > 0 ? '▲' : ''
            } ${result.deltas.fuelPerTon.toFixed(4)}`
          : `-`,
      deltaType:
        result.plan.fuelPerTon > 0
          ? result.deltas.fuelPerTon > 0
            ? 'red'
            : 'grey'
          : 'grey', // Bad
    },
    {
      icon: LocalGasStation,
      label: t('fuel_usage'),
      planValue: result.plan.totalFuel.toFixed(2),
      planUnit: unitSystem === 'metric' ? 'L' : 'Gals',
      actualValue: result.actual.totalFuel.toFixed(2),
      actualUnit: unitSystem === 'metric' ? 'L' : 'Gals',
      delta:
        result.plan.totalFuel > 0
          ? `${result.deltas.fuel > 0 ? '+' : ''} ${result.deltas.fuel.toFixed(
              2
            )}`
          : '-',
      deltaType: 'grey',
    },
  ];
  return (
    <Grid container spacing={2}>
      {kpis.map((kpi) => (
        <Grid item key={kpi.label} xs={12} sm={6} md={4} lg={2.4}>
          <FleetKPICard {...kpi} />
        </Grid>
      ))}
    </Grid>
  );
};

export const KPICardAndChartWrapper = ({
  tripsData,
  selectedRange,
}: {
  tripsData: Record<string, GeoTrip[]>;
  selectedRange: string;
}) => {
  const { t } = useTranslation();
  const { customerCode, unitSystem, customerTz } = useCustomerInfo();

  const formattedMasterAlgoTripsData = useMemo(
    () => transformMasterAlgoToPlanDataByBumperNo(tripsData),
    [tripsData]
  );
  const approvedPlanData = useAppSelector(
    (state) => state.fleetPlanningReducer.dispatchPlanSummary
  );

  const mergedPlans = mergeDispatchPlans(approvedPlanData.plans);

  const { groupedMetrics } = useMemo(() => {
    if (selectedRange === 'Today') {
      // get the end of master algo
      const { masterRange } = getPlanTimeRange(
        formattedMasterAlgoTripsData.planData,
        mergedPlans.planData,
        customerTz,
        false
      );

      // filter
      const filteredSchedule = mergedPlans.planData.filter((el) => {
        if (!el.start_ts) return false;
        const startUTC = dayjs.utc(el.start_ts).valueOf(); //  stays UTC

        return startUTC <= masterRange.endDateWithTime;
      });

      const temp = aggregateSchedulePlanByBumperNumber(filteredSchedule);
      return temp;
    } else {
      const temp = aggregateSchedulePlanByBumperNumber(mergedPlans.planData);
      return temp;
    }
  }, [
    mergedPlans.planData,
    formattedMasterAlgoTripsData.planData,
    selectedRange,
  ]);

  const convertedFuelData = useMemo(() => {
    const convertedFuelUsage: Record<
      string,
      DispatchPlanFuelUsageMetricsResponseType
    > = {};

    for (const [bumperNumber, usageData] of Object.entries(
      groupedMetrics.fuel_usage
    )) {
      const convertedData: DispatchPlanFuelUsageMetricsResponseType = {
        bumper_number: '',
        dumping_fuel: 0,
        idling_fuel: 0,
        loading_fuel: 0,
        moving_loaded_fuel: 0,
        moving_unloaded_fuel: 0,
      };

      (
        Object.keys(convertedData) as Array<
          keyof DispatchPlanFuelUsageMetricsResponseType
        >
      ).forEach((key) => {
        if (key === 'bumper_number') {
          convertedData[key] = usageData[key];
        } else {
          convertedData[key] =
            unitSystem === 'metric'
              ? Number(usageData[key])
              : convertLitersToGallons(Number(usageData[key]));
        }
      });

      convertedFuelUsage[bumperNumber] = convertedData;
    }

    return convertedFuelUsage;
  }, [groupedMetrics, unitSystem]);

  return (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <KPICardWrapper
        masterAlgoFuelUsageByBumperNo={formattedMasterAlgoTripsData.fuel_usage}
        masterAlgoTripsCountByBumperNo={
          formattedMasterAlgoTripsData.trips_count
        }
        masterAlgoMaterialDumpByBumperNo={
          formattedMasterAlgoTripsData.material_dump
        }
        masterAlgoTravelTimeByBumperNo={
          formattedMasterAlgoTripsData.travel_time
        }
        approvedPlanFuelUsageByBumperNo={convertedFuelData}
        approvedPlanMaterialDumpByBumperNo={groupedMetrics.material_dumped}
        approvedPlanTravelTimeByBumperNo={groupedMetrics.travel_time}
        approvedPlanTripsCountByBumperNo={groupedMetrics.trip}
      />
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
            {t('core_target_analysis_actual_vs_approved_plan')}
          </Typography>

          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: '0.8rem',
            }}
          >
            {t('this_analysis_shows_the_performance_of_your_primary_kpis_aga')}
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <FleetKPIStackedBarChart
              masterData={formattedMasterAlgoTripsData.material_dump}
              approvedData={groupedMetrics.material_dumped}
              title={t('tons_moved')}
              unit="T"
              stackConfig={[
                { key: 'L0', label: t('site_l0'), color: '#1565C0' },
                { key: 'L1', label: t('site_l1'), color: '#FF9800' },
                { key: 'outsideMaterial', label: t('other'), color: '#9CA3AF' },
              ]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FleetKPIStackedBarChart
              masterData={formattedMasterAlgoTripsData.travel_time}
              approvedData={groupedMetrics.travel_time}
              title={t('total_time')}
              unit="Mins"
              stackConfig={[
                { key: 'loading_mins', label: t('loading'), color: '#42C3FF' },
                {
                  key: 'moving_loaded_mins',
                  label: 'Loaded Trip',
                  color: '#2167B2',
                },
                { key: 'dumping_mins', label: t('dumping'), color: '#4AD8C9' },
                {
                  key: 'moving_unloaded_mins',
                  label: 'Unloaded Trip',
                  color: '#38B2A7',
                },
              ]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FleetKPIStackedBarChart
              masterData={formattedMasterAlgoTripsData.fuel_usage}
              approvedData={convertedFuelData}
              title={t('fuel_usage')}
              unit={unitSystem === 'metric' ? 'L' : 'Gals'}
              stackConfig={[
                { key: 'loading_fuel', label: t('loading'), color: '#42C3FF' },
                {
                  key: 'moving_loaded_fuel',
                  label: 'Loaded Trip',
                  color: '#2167B2',
                },
                { key: 'dumping_fuel', label: t('dumping'), color: '#4AD8C9' },
                {
                  key: 'moving_unloaded_fuel',
                  label: 'Unloaded Trip',
                  color: '#38B2A7',
                },
              ]}
            />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};
